/**
 * Google Analytics 4 (GA4) Integration for Life Itself Studio
 *
 * This module provides comprehensive GA4 tracking including:
 * - Enhanced e-commerce events (view_item, add_to_cart, begin_checkout, purchase, refund)
 * - Custom dimensions for plant categories and customer segments
 * - Conversion goals for key actions
 * - User properties for logged-in customers
 * - Scroll depth and engagement tracking
 * - Consent management integration
 *
 * NOTE: GA4 initialization and consent management is handled by @/lib/cookies.ts
 * This module focuses on tracking events and helper functions.
 */

// GA4 Measurement ID - supports both naming conventions
const GA4_MEASUREMENT_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || import.meta.env.VITE_GA_MEASUREMENT_ID || '';

// Type definitions for GA4 events
export interface GA4Item {
  item_id: string;
  item_name: string;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_variant?: string;
  price?: number;
  quantity?: number;
  item_brand?: string;
}

interface GA4EcommerceEvent {
  currency?: string;
  value?: number;
  items?: GA4Item[];
  coupon?: string;
  shipping?: number;
  tax?: number;
  transaction_id?: string;
  affiliation?: string;
}

export interface GA4UserProperties {
  customer_type?: 'new' | 'returning' | 'repeat';
  preferred_biome?: string;
  state?: string;
  has_insurance?: boolean;
  total_orders?: number;
  customer_segment?: string;
}

/**
 * Initialize Google Analytics 4 with consent mode defaults
 * This sets up the initial consent state before user makes a choice
 * The actual GA4 loading is handled by @/lib/cookies.ts after consent
 *
 * Should be called on app startup (before cookie consent is collected)
 */
export function initGA4(): void {
  if (!GA4_MEASUREMENT_ID) {
    console.warn('[GA4] VITE_GA4_MEASUREMENT_ID not set, analytics disabled');
    return;
  }

  // Check if gtag is already initialized
  if (typeof window.gtag === 'function') {
    console.log('[GA4] Already initialized');
    return;
  }

  // Check for existing consent from cookie consent system
  let analyticsConsent = false;
  let marketingConsent = false;

  const storedConsent = localStorage.getItem('btw_cookie_consent');
  if (storedConsent) {
    try {
      const consent = JSON.parse(storedConsent);
      analyticsConsent = consent.analytics ?? false;
      marketingConsent = consent.marketing ?? false;
    } catch (e) {
      console.error('[GA4] Failed to parse stored consent:', e);
    }
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  // Define gtag function
  window.gtag = function gtag(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  // Set default consent state (based on stored preferences or denied if none)
  window.gtag('consent', 'default', {
    analytics_storage: analyticsConsent ? 'granted' : 'denied',
    ad_storage: marketingConsent ? 'granted' : 'denied',
    ad_user_data: marketingConsent ? 'granted' : 'denied',
    ad_personalization: marketingConsent ? 'granted' : 'denied',
    functionality_storage: 'granted',
    personalization_storage: analyticsConsent ? 'granted' : 'denied',
    security_storage: 'granted',
    wait_for_update: 500, // Wait for consent update before sending hits
  });

  console.log(`[GA4] Consent defaults set, waiting for user choice or stored consent`);

  // If user has already consented, the cookies.ts initializeAnalytics will load GA4
  // If not, GA4 will be loaded when user accepts cookies
}

// =============================================================================
// Core Tracking Functions
// =============================================================================

/**
 * Track page view
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: pageTitle || document.title,
    page_location: window.location.href,
  });
}

/**
 * Track custom event
 */
export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', eventName, params);
}

// =============================================================================
// Enhanced E-commerce Events
// =============================================================================

/**
 * Track view_item event when user views a product
 */
export function trackViewItem(
  itemId: string,
  itemName: string,
  category: string,
  price: number,
  variant?: string
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  const items: GA4Item[] = [{
    item_id: itemId,
    item_name: itemName,
    item_category: category,
    item_variant: variant,
    item_brand: 'Life Itself Studio',
    price,
    quantity: 1,
  }];

  window.gtag('event', 'view_item', {
    currency: 'USD',
    value: price,
    items,
  });
}

/**
 * Track add_to_cart event
 */
export function trackAddToCart(
  itemId: string,
  itemName: string,
  category: string,
  price: number,
  quantity: number = 1,
  variant?: string
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  const items: GA4Item[] = [{
    item_id: itemId,
    item_name: itemName,
    item_category: category,
    item_variant: variant,
    item_brand: 'Life Itself Studio',
    price,
    quantity,
  }];

  window.gtag('event', 'add_to_cart', {
    currency: 'USD',
    value: price * quantity,
    items,
  });
}

/**
 * Track remove_from_cart event
 */
export function trackRemoveFromCart(
  itemId: string,
  itemName: string,
  category: string,
  price: number,
  quantity: number = 1
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  const items: GA4Item[] = [{
    item_id: itemId,
    item_name: itemName,
    item_category: category,
    item_brand: 'Life Itself Studio',
    price,
    quantity,
  }];

  window.gtag('event', 'remove_from_cart', {
    currency: 'USD',
    value: price * quantity,
    items,
  });
}

/**
 * Track begin_checkout event
 */
export function trackBeginCheckout(
  items: GA4Item[],
  value: number,
  coupon?: string
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'begin_checkout', {
    currency: 'USD',
    value,
    items,
    coupon,
  });
}

/**
 * Track checkout progress (custom event for step tracking)
 */
export function trackCheckoutStep(
  stepNumber: number,
  stepName: string,
  items?: GA4Item[],
  value?: number
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'checkout_progress', {
    checkout_step: stepNumber,
    checkout_step_name: stepName,
    currency: 'USD',
    value,
    items,
  });
}

/**
 * Track add_shipping_info event
 */
export function trackAddShippingInfo(
  items: GA4Item[],
  value: number,
  shippingTier?: string
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'add_shipping_info', {
    currency: 'USD',
    value,
    shipping_tier: shippingTier || 'local_delivery',
    items,
  });
}

/**
 * Track add_payment_info event
 */
export function trackAddPaymentInfo(
  items: GA4Item[],
  value: number,
  paymentType?: string
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'add_payment_info', {
    currency: 'USD',
    value,
    payment_type: paymentType || 'credit_card',
    items,
  });
}

/**
 * Track purchase event
 */
export function trackPurchase(
  transactionId: string,
  value: number,
  items: GA4Item[],
  options?: {
    tax?: number;
    shipping?: number;
    coupon?: string;
    affiliation?: string;
  }
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'purchase', {
    transaction_id: transactionId,
    currency: 'USD',
    value,
    items,
    tax: options?.tax || 0,
    shipping: options?.shipping || 0,
    coupon: options?.coupon,
    affiliation: options?.affiliation || 'Life Itself Studio',
  });
}

/**
 * Track refund event
 */
export function trackRefund(
  transactionId: string,
  value?: number,
  items?: GA4Item[]
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  const eventParams: GA4EcommerceEvent = {
    transaction_id: transactionId,
    currency: 'USD',
  };

  if (value !== undefined) {
    eventParams.value = value;
  }

  if (items) {
    eventParams.items = items;
  }

  window.gtag('event', 'refund', eventParams);
}

// =============================================================================
// Custom Dimensions & User Properties
// =============================================================================

/**
 * Set custom dimensions for plant categories
 */
export function setPlantCategoryDimension(biome: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('set', { biome_category: biome });
  trackEvent('set_biome_preference', { biome });
}

/**
 * Set customer segment dimension
 */
export function setCustomerSegment(segment: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('set', { customer_segment: segment });
}

/**
 * Set user properties for logged-in customers
 */
export function setUserProperties(properties: GA4UserProperties): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('set', 'user_properties', {
    customer_type: properties.customer_type,
    preferred_biome: properties.preferred_biome,
    state: properties.state,
    has_insurance: properties.has_insurance ? 'yes' : 'no',
    total_orders: properties.total_orders?.toString(),
    customer_segment: properties.customer_segment,
  });
}

/**
 * Set user ID for logged-in users
 */
export function setUserId(userId: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('set', { user_id: userId });
}

/**
 * Clear user ID on logout
 */
export function clearUserId(): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('set', { user_id: null });
}

// =============================================================================
// Conversion Goals
// =============================================================================

/**
 * Track newsletter signup conversion
 */
export function trackNewsletterSignup(source?: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'generate_lead', {
    lead_source: source || 'newsletter',
    currency: 'USD',
    value: 5, // Estimated lead value
  });

  // Also track as custom conversion
  trackEvent('newsletter_signup', { source });
}

/**
 * Track waitlist signup conversion
 */
export function trackWaitlistSignup(
  state: string,
  biomePreference?: string
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'generate_lead', {
    lead_source: 'waitlist',
    currency: 'USD',
    value: 10, // Higher value as more qualified lead
  });

  trackEvent('waitlist_signup', {
    state,
    biome_preference: biomePreference,
  });
}

/**
 * Track contact form submission
 */
export function trackContactFormSubmit(formType: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  trackEvent('contact_form_submit', { form_type: formType });
}

/**
 * Track account creation
 */
export function trackSignUp(method: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'sign_up', { method });
}

/**
 * Track login
 */
export function trackLogin(method: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'login', { method });
}

// =============================================================================
// Engagement Tracking
// =============================================================================

/**
 * Initialize scroll depth tracking
 * Tracks when user scrolls to 25%, 50%, 75%, and 100% of page
 */
export function initScrollDepthTracking(): () => void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return () => {};

  const thresholds = [25, 50, 75, 100];
  const trackedThresholds = new Set<number>();

  const handleScroll = () => {
    const scrollTop = window.scrollY;
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercent = Math.round((scrollTop / docHeight) * 100);

    thresholds.forEach((threshold) => {
      if (scrollPercent >= threshold && !trackedThresholds.has(threshold)) {
        trackedThresholds.add(threshold);
        trackEvent('scroll', {
          percent_scrolled: threshold,
          page_path: window.location.pathname,
        });
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // Return cleanup function
  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
}

/**
 * Track video engagement
 */
export function trackVideoEngagement(
  videoId: string,
  videoTitle: string,
  action: 'play' | 'pause' | 'complete' | 'progress',
  percentWatched?: number
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  const eventName = action === 'play' ? 'video_start' :
                    action === 'complete' ? 'video_complete' :
                    'video_progress';

  window.gtag('event', eventName, {
    video_provider: 'self_hosted',
    video_title: videoTitle,
    video_url: videoId,
    video_percent: percentWatched,
  });
}

/**
 * Track time on page / engagement time
 */
export function initEngagementTimeTracking(): () => void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return () => {};

  const startTime = Date.now();
  const intervals = [30, 60, 120, 300]; // seconds
  const trackedIntervals = new Set<number>();
  let timer: ReturnType<typeof setInterval>;

  timer = setInterval(() => {
    const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);

    intervals.forEach((interval) => {
      if (elapsedSeconds >= interval && !trackedIntervals.has(interval)) {
        trackedIntervals.add(interval);
        trackEvent('engagement_time', {
          engagement_time_seconds: interval,
          page_path: window.location.pathname,
        });
      }
    });

    // Clear interval after all thresholds tracked
    if (trackedIntervals.size === intervals.length) {
      clearInterval(timer);
    }
  }, 5000);

  return () => {
    clearInterval(timer);
  };
}

/**
 * Track outbound link clicks
 */
export function trackOutboundLink(url: string, linkText?: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  trackEvent('click', {
    link_url: url,
    link_text: linkText,
    outbound: true,
  });
}

/**
 * Track file downloads
 */
export function trackFileDownload(
  fileName: string,
  fileExtension: string
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'file_download', {
    file_name: fileName,
    file_extension: fileExtension,
  });
}

/**
 * Track search queries
 */
export function trackSearch(searchTerm: string): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'search', {
    search_term: searchTerm,
  });
}

/**
 * Track share events
 */
export function trackShare(
  method: string,
  contentType: string,
  itemId?: string
): void {
  if (!GA4_MEASUREMENT_ID || !window.gtag) return;

  window.gtag('event', 'share', {
    method,
    content_type: contentType,
    item_id: itemId,
  });
}

// =============================================================================
// Helper Functions for Life Itself Studio E-commerce
// =============================================================================

/**
 * Build GA4 item from checkout state
 */
export function buildGA4Item(
  biome: string,
  hasPremiumPlants: boolean,
  hasCustomDesign: boolean,
  experienceType: 'none' | '2-person' | '4-person'
): GA4Item[] {
  const items: GA4Item[] = [];

  // Base terrarium
  items.push({
    item_id: `terrarium-${biome}`,
    item_name: `${biome.charAt(0).toUpperCase() + biome.slice(1)} Terrarium`,
    item_category: 'Terrariums',
    item_category2: biome,
    item_brand: 'Life Itself Studio',
    price: 499,
    quantity: 1,
  });

  // Premium plants upgrade
  if (hasPremiumPlants) {
    items.push({
      item_id: 'upgrade-premium-plants',
      item_name: 'Premium Plants Upgrade',
      item_category: 'Upgrades',
      item_category2: 'Plants',
      item_brand: 'Life Itself Studio',
      price: 499,
      quantity: 1,
    });
  }

  // Custom design upgrade
  if (hasCustomDesign) {
    items.push({
      item_id: 'upgrade-custom-design',
      item_name: 'Custom Design Consultation',
      item_category: 'Upgrades',
      item_category2: 'Design',
      item_brand: 'Life Itself Studio',
      price: 999,
      quantity: 1,
    });
  }

  // Experience add-on
  if (experienceType !== 'none') {
    const experiencePrice = experienceType === '2-person' ? 1501 : 3501;
    items.push({
      item_id: `experience-${experienceType}`,
      item_name: `Nature Experience (${experienceType})`,
      item_category: 'Experiences',
      item_category2: experienceType,
      item_brand: 'Life Itself Studio',
      price: experiencePrice,
      quantity: 1,
    });
  }

  return items;
}

/**
 * Calculate total value from items
 */
export function calculateItemsValue(items: GA4Item[]): number {
  return items.reduce((total, item) => {
    return total + (item.price || 0) * (item.quantity || 1);
  }, 0);
}
